import React, { useEffect, useState, useCallback, useMemo, memo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Collapse,
  Paper,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import { FaEdit, FaPaperclip, FaSave, FaTimes } from "react-icons/fa";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { jwtDecode } from "jwt-decode";
import muiTheme from "../theme/muiTheme";
import { copiarTexto } from "../utils/clipboard";
import CampoSelecionar from "../components/CampoSelecionar";
import ModalSelecionarProposta from "../components/ModalSelecionarProposta";
import ModalSelecionarTemplate from "../components/ModalSelecionarTemplate";
import PainelAprovacao from "../components/Aprovacao/PainelAprovacao";
import DialogReprovarAprovacao from "../components/Aprovacao/DialogReprovarAprovacao";
import DialogQuestionarAprovacao from "../components/Aprovacao/DialogQuestionarAprovacao";
import DialogResponderAprovacao from "../components/Aprovacao/DialogResponderAprovacao";

function formatarData(val) {
  if (!val) return "—";
  const s = String(val).substring(0, 10);
  const [ano, mes, dia] = s.split("-").map(Number);
  if (!ano || !mes || !dia) return "—";
  return `${String(dia).padStart(2, "0")}/${String(mes).padStart(2, "0")}/${ano}`;
}

function formatarDataHora(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_APROVACAO_CONTRATO_CONFIG = {
  EmElaboracao: { label: "Em Elaboração", cor: "#555" },
  EmAprovacao: { label: "Em Aprovação", cor: "#1998a8" },
  Aprovado: { label: "Aprovado", cor: "#2e7d32" },
  Reprovado: { label: "Reprovado", cor: "#d32f2f" },
  Inutilizado: { label: "Inutilizado", cor: "#4e342e" },
  Arquivado: { label: "Arquivado", cor: "#795548" },
};
function chipStatusAprovacaoContrato(status) {
  const cfg = STATUS_APROVACAO_CONTRATO_CONFIG[status] ?? { label: status ?? "—", cor: "#444" };
  return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.cor, color: "#fff", fontWeight: "bold" }} />;
}

// statusFluxo do processo de Assinatura Final (dbo.AssinaturaProcesso) vinculado ao contrato
const STATUS_ASSINATURA_CONFIG = {
  RASCUNHO: { label: "Rascunho", cor: "#555" },
  EM_ASSINATURA_FISICA: { label: "Em Assinatura Física", cor: "#ed6c02" },
  ENVIADO: { label: "Enviado", cor: "#1976d2" },
  AGUARDANDO_ASSINATURAS: { label: "Aguard. Assinaturas", cor: "#7b1fa2" },
  PARCIALMENTE_ASSINADO: { label: "Parcialmente Assinado", cor: "#ed6c02" },
  ASSINADO: { label: "Assinado", cor: "#2e7d32" },
  ARQUIVADO: { label: "Arquivado", cor: "#004d40" },
  CANCELADO: { label: "Cancelado", cor: "#d32f2f" },
};

function chipStatusAssinatura(status) {
  if (!status) return <Chip label="—" size="small" sx={{ backgroundColor: "#444", color: "#aaa" }} />;
  const cfg = STATUS_ASSINATURA_CONFIG[status] ?? { label: status, cor: "#444" };
  return <Chip label={cfg.label} size="small" sx={{ backgroundColor: cfg.cor, color: "#fff", fontWeight: "bold" }} />;
}

const CONTRATO_NOVO = { __novo: true };

// A informação deve ser fornecida pelo backend com base
// no histórico completo de aprovação do contrato.
// Na ausência da informação, nenhuma ação destrutiva é liberada.
function obterAcaoRetiradaContrato(contrato) {
  if (!contrato || ["Inutilizado", "Arquivado"].includes(contrato.statusAprovacao)) {
    return null;
  }

  if (contrato.statusAprovacao === "EmElaboracao" && contrato.jaEnviadoAprovacao === false) {
    return "excluir";
  }

  if (contrato.jaEnviadoAprovacao === true) {
    return "arquivar";
  }

  return null;
}

const SOLICITANTES_VERSAO = ["Locador", "Locatário", "Parte Garantidora"];

// ── Filtro vindo do Dashboard de Contratos (pendência de aprovação por usuário) ─
const LABEL_PENDENCIA_APROVACAO = { Aprovar: "A Aprovar", Responder: "A Responder", Aguardando: "Aguardando Aprovação" };

// ── Subcomponente: dialog de Invalidar (estado local do textarea — evita que
// cada tecla digitada re-renderize o formulário inteiro de Contratos)
// Popup de erro ao gerar o contrato a partir de um Template (.docx) — usado quando o
// backend acusa problema num marcador do template (ver gerarContratoDeTemplate.js).
// Cada item de `detalhes` já vem em PT-BR, com o parágrafo e um trecho do documento
// pra o usuário localizar (Ctrl+F no Word) e corrigir o marcador problemático.
function DialogErroTemplate({ aberto, onFechar, detalhes }) {
  return (
    <Dialog
      open={aberto}
      onClose={onFechar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "var(--ig-f-1e1e1e)", color: "var(--ig-t-ffffff)" } }}
    >
      <DialogTitle sx={{ color: "#f44336" }}>Erro ao gerar o contrato a partir do template</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <DialogContentText sx={{ color: "var(--ig-t-aaaaaa)" }}>
          O arquivo .docx do Template tem um problema num marcador de tag. Abra o Template no Word, use Localizar (Ctrl+F) para achar o trecho indicado abaixo e
          corrija.
        </DialogContentText>
        {(detalhes || []).map((d, idx) => (
          <Box key={idx} sx={{ backgroundColor: "var(--ig-f-2a2a2a)", border: "1px solid var(--ig-b-444444)", borderRadius: 1, p: 1.5 }}>
            <Typography sx={{ color: "var(--ig-t-ffffff)", fontSize: 14 }}>{d.mensagem}</Typography>
            {d.paragrafo != null && (
              <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: 13, mt: 1 }}>
                Aproximadamente no parágrafo {d.paragrafo} do documento
                {d.trecho ? (
                  <>
                    , perto do trecho:{" "}
                    <Box component="span" sx={{ color: "var(--ig-t-ffffff)", fontFamily: "monospace" }}>
                      "{d.trecho}"
                    </Box>
                  </>
                ) : (
                  "."
                )}
              </Typography>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onFechar}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DialogInutilizarContrato({ aberto, onCancelar, onConfirmar }) {
  const [motivo, setMotivo] = useState("");

  useEffect(() => {
    if (aberto) setMotivo("");
  }, [aberto]);

  return (
    <Dialog
      open={aberto}
      onClose={onCancelar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "var(--ig-f-1e1e1e)", color: "var(--ig-t-ffffff)" } }}
    >
      <DialogTitle sx={{ color: "var(--ig-t-ffffff)" }}>Inutilizar Contrato</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <DialogContentText sx={{ color: "var(--ig-t-aaaaaa)" }}>
          Contratos não podem ser excluídos. Informe o motivo pelo qual este contrato será inutilizado — esta ação não pode ser desfeita.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          size="small"
          multiline
          rows={6}
          label="Motivo *"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          helperText={`${motivo.length}/255`}
          slotProps={{ htmlInput: { maxLength: 255 } }}
          sx={{
            "& .MuiInputLabel-root": { color: "var(--ig-t-ffffff)" },
            "& .MuiOutlinedInput-root": {
              backgroundColor: "var(--ig-f-2a2a2a)",
              color: "var(--ig-t-ffffff)",
              "& fieldset": { borderColor: "var(--ig-b-444444)" },
              "&:hover fieldset": { borderColor: "var(--ig-b-888888)" },
            },
            "& textarea": { color: "var(--ig-t-ffffff)" },
            "& .MuiFormHelperText-root": { color: "var(--ig-t-888888)" },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button variant="contained" color="error" onClick={() => onConfirmar(motivo)}>
          Inutilizar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Subcomponente: grade de contratos ────────────────────────────────────────
const GridContratos = memo(function GridContratos({
  gridMinimizado,
  carregando,
  items,
  total,
  pagina,
  porPagina,
  contratoSelecionadoId,
  podeVisualizar,
  podeExcluir,
  onVisualizar,
  onAbrirExcluir,
  onAbrirArquivar,
  onAbrirAtivar,
  setPagina,
  setPorPagina,
}) {
  return (
    <Collapse in={!gridMinimizado}>
      <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
        {carregando ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Table size="small" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Ações
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Versões
                </TableCell>
                <TableCell
                  sx={{ backgroundColor: "var(--ig-f-1e1e1e)", color: "var(--ig-t-ffffff)", fontWeight: "bold", borderBottom: "1px solid var(--ig-b-444444)" }}
                >
                  Proposta vinculada
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    width: "100%",
                  }}
                >
                  Título
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Aprovação
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status Assinatura
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Última Versão
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "var(--ig-f-1e1e1e)",
                    color: "var(--ig-t-ffffff)",
                    fontWeight: "bold",
                    borderBottom: "1px solid var(--ig-b-444444)",
                    whiteSpace: "nowrap",
                  }}
                >
                  Solicitante
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                    Nenhum contrato cadastrado.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                      backgroundColor: contratoSelecionadoId === item.id ? "rgba(25,152,168,0.08)" : "transparent",
                    }}
                  >
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                      {podeVisualizar && (
                        <Tooltip title="Visualizar contrato">
                          <IconButton
                            size="small"
                            onClick={() => onVisualizar(item)}
                            sx={{
                              backgroundColor: "#1998a8",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#1565c0" },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {podeExcluir && obterAcaoRetiradaContrato(item) === "excluir" && (
                        <Tooltip title="Excluir contrato antes do primeiro envio para aprovação">
                          <IconButton
                            size="small"
                            onClick={() => onAbrirExcluir(item.id)}
                            sx={{
                              backgroundColor: "#d32f2f",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#c62828" },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {podeExcluir && obterAcaoRetiradaContrato(item) === "arquivar" && (
                        <Tooltip title="Arquivar contrato">
                          <IconButton
                            size="small"
                            onClick={() => onAbrirArquivar(item.id)}
                            sx={{
                              backgroundColor: "#795548",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#5d4037" },
                            }}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {podeExcluir && item.statusAprovacao === "Inutilizado" && (
                        <Tooltip title="Ativar">
                          <IconButton
                            size="small"
                            onClick={() => onAbrirAtivar(item.id, item.motivoInutilizacao)}
                            sx={{
                              backgroundColor: "#2e7d32",
                              color: "#fff",
                              borderRadius: "4px",
                              mx: 0.25,
                              width: "auto",
                              "&:hover": { backgroundColor: "#1b5e20" },
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-aaaaaa)" }}>
                      {item._count?.versoes ?? 0}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-aaaaaa)" }}>{item.proposta?.codigo ?? "—"}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", fontWeight: "bold", width: "100%" }}>
                      {item.titulo ?? "—"}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusAprovacaoContrato(item.statusAprovacao)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)" }}>{chipStatusAssinatura(item.statusAssinatura)}</TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-aaaaaa)", whiteSpace: "nowrap" }}>
                      {formatarData(item.versoes?.[0]?.dataUpload)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-aaaaaa)", whiteSpace: "nowrap" }}>
                      {item.versoes?.[0]?.solicitante ?? "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        {!carregando && (
          <TablePagination
            component="div"
            count={total}
            page={pagina}
            onPageChange={(_, p) => setPagina(p)}
            rowsPerPage={porPagina}
            onRowsPerPageChange={(e) => {
              setPorPagina(parseInt(e.target.value, 10));
              setPagina(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            sx={{
              color: "var(--ig-t-ffffff)",
              borderTop: "1px solid var(--ig-b-444444)",
              "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
              "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
              "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
            }}
          />
        )}
      </TableContainer>
    </Collapse>
  );
});

// ── Subcomponente: tabela de versões do contrato ─────────────────────────────
const TabelaVersoesContrato = memo(function TabelaVersoesContrato({
  versoesContrato,
  usuariosPorId,
  contratoId,
  statusAprovacao,
  jaEnviadoAprovacao,
  podeExcluir,
  visualizandoContrato,
  onVisualizarDocumento,
  onBaixarArquivo,
  onExcluirVersao,
}) {
  return (
    <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
      <Table size="small" sx={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
              }}
            >
              Versão
            </TableCell>
            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
              }}
            >
              Arquivo
            </TableCell>
            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
              }}
            >
              Notas da Alteração
            </TableCell>
            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
                whiteSpace: "nowrap",
              }}
            >
              Data
            </TableCell>
            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
              }}
            >
              Enviado por
            </TableCell>
            <TableCell
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
              }}
            >
              Solicitante
            </TableCell>
            <TableCell
              align="right"
              sx={{
                backgroundColor: "var(--ig-f-252525)",
                color: "var(--ig-t-aaaaaa)",
                fontWeight: "bold",
                borderBottom: "1px solid var(--ig-b-444444)",
                py: 0.8,
                whiteSpace: "nowrap",
              }}
            >
              Ações
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {versoesContrato.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={7}
                align="center"
                sx={{ color: "var(--ig-t-666666)", fontStyle: "italic", borderBottom: "1px solid var(--ig-b-333333)", py: 3 }}
              >
                Nenhuma versão de contrato enviada ainda.
              </TableCell>
            </TableRow>
          ) : (
            versoesContrato.map((v, idx) => (
              <TableRow key={v.id} sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-ffffff)", fontWeight: "bold" }}>v{v.numeroVersao}</TableCell>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-cccccc)" }}>{v.nomeOriginal}</TableCell>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-cccccc)", maxWidth: 340, whiteSpace: "pre-wrap" }}>
                  {v.notasAlteracao}
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-aaaaaa)", whiteSpace: "nowrap" }}>
                  {formatarData(v.dataUpload)}
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-aaaaaa)" }}>
                  {usuariosPorId.get(v.idUsuario)?.nome ?? "—"}
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-aaaaaa)" }}>{v.solicitante ?? "—"}</TableCell>
                <TableCell align="right" sx={{ borderBottom: "1px solid var(--ig-b-333333)", whiteSpace: "nowrap" }}>
                  <Tooltip title="Visualizar PDF">
                    <IconButton
                      size="small"
                      onClick={() => onVisualizarDocumento(contratoId, v.id)}
                      disabled={visualizandoContrato === v.id}
                      sx={{ color: "var(--ig-t-64b5f6)", borderRadius: "4px", mx: 0.25, width: "auto", "&.Mui-disabled": { color: "#555" } }}
                    >
                      {visualizandoContrato === v.id ? (
                        <CircularProgress size={16} sx={{ color: "var(--ig-t-64b5f6)" }} />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Baixar .docx">
                    <IconButton
                      size="small"
                      onClick={() => onBaixarArquivo(`/api/contratos/${contratoId}/versoes/${v.id}/download`, v.nomeOriginal)}
                      sx={{ backgroundColor: "#555", color: "#fff", borderRadius: "4px", mx: 0.25, width: "auto", "&:hover": { backgroundColor: "#444" } }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Baixar PDF">
                    <IconButton
                      size="small"
                      onClick={() => onBaixarArquivo(`/api/contratos/${contratoId}/versoes/${v.id}/download-pdf`, v.nomeOriginal.replace(/\.docx$/i, ".pdf"))}
                      sx={{
                        backgroundColor: "#1998a8",
                        color: "#fff",
                        borderRadius: "4px",
                        mx: 0.25,
                        width: "auto",
                        "&:hover": { backgroundColor: "#1565c0" },
                      }}
                    >
                      <PictureAsPdfIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {idx === 0 && podeExcluir && statusAprovacao === "EmElaboracao" && jaEnviadoAprovacao === false && (
                    <Tooltip title="Excluir esta versão (só a mais recente pode ser excluída)">
                      <IconButton
                        size="small"
                        onClick={() => onExcluirVersao(v.id)}
                        sx={{
                          backgroundColor: "#d32f2f",
                          color: "#fff",
                          borderRadius: "4px",
                          mx: 0.25,
                          width: "auto",
                          "&:hover": { backgroundColor: "#c62828" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export default function Contratos() {
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  let userId = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tipoUsuario = decoded.tipo?.toString().trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
      userId = decoded.id ?? null;
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  // ── Grid de contratos ────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(5);
  const [gridMinimizado, setGridMinimizado] = useState(false);

  // ── Filtro vindo do Dashboard de Contratos (via query params)
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtroDashboard, setFiltroDashboard] = useState(() => {
    const pendenciaAprovacao = searchParams.get("pendenciaAprovacao");
    const meusContratos = searchParams.get("meusContratos") === "true";
    const escopoGeral = searchParams.get("escopo") === "geral";
    if (pendenciaAprovacao) return { tipo: "pendenciaAprovacao", valor: pendenciaAprovacao, geral: escopoGeral };
    if (meusContratos) return { tipo: "meusContratos", valor: true };
    return null;
  });

  const [contratoSelecionado, setContratoSelecionado] = useState(CONTRATO_NOVO);
  const [usuariosAtivos, setUsuariosAtivos] = useState([]);
  const [usuariosAprovadores, setUsuariosAprovadores] = useState([]);

  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const notasContratoRef = useRef(null);

  const [versoesContrato, setVersoesContrato] = useState([]);
  const [arquivoContrato, setArquivoContrato] = useState(null);
  const [notasContrato, setNotasContrato] = useState("");
  const [solicitanteContrato, setSolicitanteContrato] = useState("");
  const [enviandoContrato, setEnviandoContrato] = useState(false);
  const [visualizandoContrato, setVisualizandoContrato] = useState(null); // id da versão sendo convertida p/ PDF
  const [confirmExcluirVersao, setConfirmExcluirVersao] = useState({ aberto: false, id: null });
  const [confirmInutilizar, setConfirmInutilizar] = useState({ aberto: false, id: null });
  const [confirmAtivar, setConfirmAtivar] = useState({ aberto: false, id: null, motivoInutilizacao: "", motivo: "" });

  const [confirmExcluirContrato, setConfirmExcluirContrato] = useState({
    aberto: false,
    id: null,
  });

  const [confirmArquivarContrato, setConfirmArquivarContrato] = useState({
    aberto: false,
    id: null,
  });
  const [processandoRetiradaContrato, setProcessandoRetiradaContrato] = useState(false);

  // ── Aprovação do contrato (fluxo e auditoria próprios) ──────────────────
  const [abaAtiva, setAbaAtiva] = useState("versoes");
  const [aprovacaoInfo, setAprovacaoInfo] = useState({ aprovacao: null, autorizacoes: [], historicos: [], usuarioEhAprovador: false, usuarioEmAlcada: false });
  const [enviandoAprovacao, setEnviandoAprovacao] = useState(false);
  const [processandoAprovacao, setProcessandoAprovacao] = useState(false);
  const [dialogReprovar, setDialogReprovar] = useState({ aberto: false, motivo: "" });
  const [dialogQuestionar, setDialogQuestionar] = useState({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null });
  const [dialogResponder, setDialogResponder] = useState({ aberto: false, resposta: "", pergunta: "", arquivo: null });
  const [confirmRevisar, setConfirmRevisar] = useState({ aberto: false, motivo: "" });

  // ── Novo contrato ────────────────────────────────────────────────────────
  const [tituloNovo, setTituloNovo] = useState("");
  const [propostaVinculada, setPropostaVinculada] = useState(null);
  const [modalPropostaAberto, setModalPropostaAberto] = useState(false);
  const [templateVinculado, setTemplateVinculado] = useState(null);
  const [modalTemplateAberto, setModalTemplateAberto] = useState(false);
  const [criandoContrato, setCriandoContrato] = useState(false);

  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = useCallback((msg, severity = "success") => setSnack({ aberto: true, msg, severity }), []);

  // Erro ao gerar o contrato a partir de um Template (.docx): quando o backend manda
  // `detalhes` (tag/marcador problemático já traduzido e localizado no documento — ver
  // gerarContratoDeTemplate.js), mostra um popup em vez do snackbar, porque a mensagem
  // costuma ser longa demais e o usuário precisa ler com calma pra corrigir o template.
  const [dialogErroTemplate, setDialogErroTemplate] = useState({ aberto: false, detalhes: [] });
  const tratarErroResposta = useCallback(
    (corpoResposta, mensagemFallback) => {
      if (Array.isArray(corpoResposta?.detalhes) && corpoResposta.detalhes.length > 0) {
        setDialogErroTemplate({ aberto: true, detalhes: corpoResposta.detalhes });
        return;
      }
      mostrarSnack(corpoResposta?.erro || mensagemFallback, "error");
    },
    [mostrarSnack],
  );

  // Mapa id → usuário — evita repetir .find() em cada linha da tabela de versões
  // e na seção de eventos de inutilização/reativação.
  const usuariosAtivosPorId = useMemo(() => new Map(usuariosAtivos.map((u) => [u.id, u])), [usuariosAtivos]);

  const carregarContratos = useCallback(() => {
    const tk = localStorage.getItem("token");
    const params = new URLSearchParams({ filtro, pagina, porPagina });
    if (filtroDashboard?.tipo === "pendenciaAprovacao") {
      params.set("pendenciaAprovacao", filtroDashboard.valor);
      if (filtroDashboard.geral) params.set("escopo", "geral");
    }
    if (filtroDashboard?.tipo === "meusContratos") params.set("meusContratos", "true");
    setCarregando(true);
    fetch(`/api/contratos?${params}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then(({ data, total: t }) => {
        setItems(Array.isArray(data) ? data : []);
        setTotal(t ?? 0);
      })
      .catch((err) => console.error("Erro ao buscar contratos:", err))
      .finally(() => setCarregando(false));
  }, [filtro, pagina, porPagina, filtroDashboard]);

  useEffect(() => {
    if (!podeVisualizar) return;
    carregarContratos();
  }, [podeVisualizar, carregarContratos]);

  // ── Pesquisa automática: aplica o filtro digitado após o usuário parar de digitar
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };

  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  // =======================
  // COPIAR
  // =======================
  const handleCopiar = async () => {
    const texto = items
      .map((item) => {
        const versoes = item._count?.versoes ?? 0;
        const proposta = item.proposta?.codigo ?? "—";
        const titulo = item.titulo ?? "—";

        const status = STATUS_APROVACAO_CONTRATO_CONFIG[item.statusAprovacao]?.label ?? item.statusAprovacao ?? "—";

        const ultimaVersao = formatarData(item.versoes?.[0]?.dataUpload);

        return [versoes, proposta, titulo, status, ultimaVersao].join(" - ");
      })
      .join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);

    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  // =======================
  // CSV
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = ["Versões", "Proposta vinculada", "Título", "Aprovação", "Última Versão"];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = items.map((item) => {
      const status = STATUS_APROVACAO_CONTRATO_CONFIG[item.statusAprovacao]?.label ?? item.statusAprovacao ?? "";

      return [item._count?.versoes ?? 0, item.proposta?.codigo ?? "", item.titulo ?? "", status, formatarData(item.versoes?.[0]?.dataUpload)].map(csvEscape);
    });

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "contratos.csv");

    document.body.appendChild(link);

    link.click();

    link.remove();

    URL.revokeObjectURL(url);
  };

  const handleLimparFiltroDashboard = () => {
    setFiltroDashboard(null);
    setPagina(0);
    setSearchParams({});
  };

  useEffect(() => {
    if (!podeVisualizar) return;
    const tk = localStorage.getItem("token");
    fetch("/api/propostas/usuarios-ativos", { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setUsuariosAtivos(Array.isArray(data) ? data : []))
      .catch(() => setUsuariosAtivos([]));
    fetch("/api/propostas/usuarios-aprovadores", { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setUsuariosAprovadores(Array.isArray(data) ? data : []))
      .catch(() => setUsuariosAprovadores([]));
  }, [podeVisualizar]);

  // ── Contrato — versionamento manual ─────────────────────────────────────
  const carregarVersoesContrato = useCallback((id) => {
    const tk = localStorage.getItem("token");
    fetch(`/api/contratos/${id}/versoes`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setVersoesContrato(Array.isArray(data) ? data : []))
      .catch(() => setVersoesContrato([]));
  }, []);

  // ── Contrato — aprovação (fluxo e auditoria próprios) ───────────────────
  const carregarAprovacao = useCallback((id) => {
    const tk = localStorage.getItem("token");
    fetch(`/api/contratos/${id}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setContratoSelecionado((prev) => (prev ? { ...prev, ...data } : prev));
        setAprovacaoInfo({
          aprovacao: (data.aprovacoes ?? []).find((a) => a.processoTipo === "Contrato") ?? null,
          autorizacoes: (data.autorizacoes ?? []).filter((a) => a.processoTipo === "Contrato"),
          historicos: (data.historicos ?? []).filter((h) => h.processoTipo === "Contrato"),
          usuarioEhAprovador: data.usuarioEhAprovador ?? false,
          usuarioEmAlcada: data.usuarioEmAlcada ?? false,
        });
      })
      .catch(() => {});
  }, []);

  const chamarAprovacao = useCallback(
    (url, body, setProcessando, msgErro) => {
      const tk = localStorage.getItem("token");
      setProcessando(true);
      const ehFormData = body instanceof FormData;
      return fetch(url, {
        method: "POST",
        headers: ehFormData ? { Authorization: `Bearer ${tk}` } : { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: ehFormData ? body : JSON.stringify(body),
      })
        .then(async (r) => {
          const d = await r.json();
          if (!r.ok) throw new Error(d.erro || msgErro);
          setContratoSelecionado((prev) => (prev ? { ...prev, ...d } : prev));
          setAprovacaoInfo({
            aprovacao: (d.aprovacoes ?? []).find((a) => a.processoTipo === "Contrato") ?? null,
            autorizacoes: (d.autorizacoes ?? []).filter((a) => a.processoTipo === "Contrato"),
            historicos: (d.historicos ?? []).filter((h) => h.processoTipo === "Contrato"),
            usuarioEhAprovador: d.usuarioEhAprovador ?? false,
            usuarioEmAlcada: d.usuarioEmAlcada ?? false,
          });
          carregarContratos();
          return d;
        })
        .catch((e) => {
          mostrarSnack(e.message || msgErro, "error");
          return null;
        })
        .finally(() => setProcessando(false));
    },
    [carregarContratos, mostrarSnack],
  );

  const handleEnviarAprovacao = () => {
    if (!contratoSelecionado || contratoSelecionado.__novo) return;
    chamarAprovacao(`/api/contratos/${contratoSelecionado.id}/enviar-aprovacao`, {}, setEnviandoAprovacao, "Erro ao enviar para aprovação.").then(
      (r) => r && mostrarSnack("Contrato enviado para aprovação!"),
    );
  };

  const handleAprovar = () => {
    chamarAprovacao(`/api/contratos/${contratoSelecionado.id}/aprovar`, {}, setProcessandoAprovacao, "Erro ao aprovar.").then(
      (r) => r && mostrarSnack("Contrato aprovado!"),
    );
  };

  const handleReprovar = () => {
    if (!dialogReprovar.motivo.trim()) return;
    chamarAprovacao(`/api/contratos/${contratoSelecionado.id}/reprovar`, { motivo: dialogReprovar.motivo }, setProcessandoAprovacao, "Erro ao reprovar.").then(
      (r) => {
        if (r) {
          setDialogReprovar({ aberto: false, motivo: "" });
          mostrarSnack("Contrato reprovado.", "warning");
        }
      },
    );
  };

  const handleQuestionar = () => {
    if (!dialogQuestionar.motivo.trim() || !dialogQuestionar.idsUsuarioResposta.length) return;
    const fd = new FormData();
    fd.append("motivo", dialogQuestionar.motivo);
    fd.append("idsUsuarioResposta", JSON.stringify(dialogQuestionar.idsUsuarioResposta));
    if (dialogQuestionar.arquivo) fd.append("arquivo", dialogQuestionar.arquivo);
    chamarAprovacao(`/api/contratos/${contratoSelecionado.id}/questionar`, fd, setProcessandoAprovacao, "Erro ao questionar.").then((r) => {
      if (r) {
        setDialogQuestionar({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null });
        mostrarSnack("Questão enviada.");
      }
    });
  };

  const handleResponder = () => {
    if (!dialogResponder.resposta.trim()) return;
    const fd = new FormData();
    fd.append("resposta", dialogResponder.resposta);
    if (dialogResponder.arquivo) fd.append("arquivo", dialogResponder.arquivo);
    chamarAprovacao(`/api/contratos/${contratoSelecionado.id}/responder`, fd, setProcessandoAprovacao, "Erro ao responder.").then((r) => {
      if (r) {
        setDialogResponder({ aberto: false, resposta: "", pergunta: "", arquivo: null });
        mostrarSnack("Resposta enviada.");
      }
    });
  };

  const handleRevisar = () => {
    const { motivo } = confirmRevisar;
    if (!motivo?.trim()) {
      mostrarSnack("Motivo é obrigatório.", "error");
      return;
    }
    chamarAprovacao(`/api/contratos/${contratoSelecionado.id}/revisar`, { motivo }, setProcessandoAprovacao, "Erro ao revisar.").then((r) => {
      if (r) {
        setConfirmRevisar({ aberto: false, motivo: "" });
        mostrarSnack("Contrato devolvido para elaboração.");
      }
    });
  };

  useEffect(() => {
    if (contratoSelecionado && !contratoSelecionado.__novo) {
      carregarVersoesContrato(contratoSelecionado.id);
      carregarAprovacao(contratoSelecionado.id);
    }
    // Depende só do id (não do objeto inteiro)
  }, [contratoSelecionado?.id, contratoSelecionado?.__novo, carregarVersoesContrato, carregarAprovacao]);

  const handleVisualizar = useCallback((item) => {
    setContratoSelecionado(item);
    setAbaAtiva("versoes");
    setAprovacaoInfo({ aprovacao: null, autorizacoes: [], historicos: [] });
    setGridMinimizado(true);
    setTemplateVinculado(null);
    setSomenteLeitura(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }, []);

  const handleHabilitarEdicao = useCallback(() => {
    setSomenteLeitura(false);
    setTimeout(() => notasContratoRef.current?.focus(), 50);
  }, []);

  const handleFecharDetalhe = useCallback(() => {
    setContratoSelecionado(CONTRATO_NOVO);
    setVersoesContrato([]);
    setArquivoContrato(null);
    setNotasContrato("");
    setSolicitanteContrato("");
    setTituloNovo("");
    setPropostaVinculada(null);
    setTemplateVinculado(null);
    setAbaAtiva("versoes");
    setAprovacaoInfo({ aprovacao: null, autorizacoes: [], historicos: [] });
    setGridMinimizado(false);
    setSomenteLeitura(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleCriarContrato = () => {
    // Com Proposta vinculada, o título é gerado automaticamente pelo backend —
    // sem Proposta, o usuário digita.
    if (!propostaVinculada && !tituloNovo.trim()) {
      mostrarSnack("Informe o título do contrato.", "warning");
      return;
    }

    const tk = localStorage.getItem("token");
    setCriandoContrato(true);

    // Com Proposta vinculada, o contrato é gerado a partir de um Template (sem upload);
    // sem Proposta, mantém o upload manual do .docx como sempre foi.
    const usaTemplate = !!propostaVinculada;
    let requisicao;
    if (usaTemplate) {
      if (!templateVinculado) {
        mostrarSnack("Selecione um Template.", "warning");
        setCriandoContrato(false);
        return;
      }
      requisicao = fetch(`/api/contratos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          propostaId: propostaVinculada.id,
          templateId: templateVinculado.id,
        }),
      });
    } else {
      if (!arquivoContrato) {
        mostrarSnack("Selecione o arquivo .docx.", "warning");
        setCriandoContrato(false);
        return;
      }
      const fd = new FormData();
      fd.append("titulo", tituloNovo.trim());
      fd.append("arquivo", arquivoContrato);
      requisicao = fetch(`/api/contratos`, { method: "POST", headers: { Authorization: `Bearer ${tk}` }, body: fd });
    }

    requisicao
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          tratarErroResposta(d, "Erro ao criar contrato.");
          return;
        }
        mostrarSnack("Contrato criado com sucesso!");
        carregarContratos();
        handleFecharDetalhe();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setCriandoContrato(false));
  };

  const handleEnviarVersaoContrato = () => {
    if (!contratoSelecionado || contratoSelecionado.__novo || !arquivoContrato) {
      mostrarSnack("Selecione o arquivo .docx.", "warning");
      return;
    }
    if (!notasContrato.trim()) {
      mostrarSnack("Descreva as alterações desta versão.", "warning");
      return;
    }
    if (!solicitanteContrato) {
      mostrarSnack("Selecione o Solicitante da revisão.", "warning");
      return;
    }
    const tk = localStorage.getItem("token");
    const fd = new FormData();
    fd.append("arquivo", arquivoContrato);
    fd.append("notasAlteracao", notasContrato);
    fd.append("solicitante", solicitanteContrato);
    setEnviandoContrato(true);
    fetch(`/api/contratos/${contratoSelecionado.id}/versoes`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tk}` },
      body: fd,
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          mostrarSnack(d.erro || "Erro ao enviar.", "error");
          return;
        }
        setArquivoContrato(null);
        setNotasContrato("");
        setSolicitanteContrato("");
        carregarVersoesContrato(contratoSelecionado.id);
        carregarContratos();
        mostrarSnack("Nova versão do contrato enviada com sucesso!");
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setEnviandoContrato(false));
  };

  // Regenera o documento a partir de um Template — só aparece quando todas as
  // versões foram excluídas de um contrato originalmente gerado por Template
  // (ver `precisaSelecionarTemplate`, calculado na renderização).
  const handleEnviarVersaoContratoViaTemplate = () => {
    if (!contratoSelecionado || contratoSelecionado.__novo || !templateVinculado) {
      mostrarSnack("Selecione um Template.", "warning");
      return;
    }
    if (!notasContrato.trim()) {
      mostrarSnack("Descreva as alterações desta versão.", "warning");
      return;
    }
    if (!solicitanteContrato) {
      mostrarSnack("Selecione o Solicitante da revisão.", "warning");
      return;
    }
    const tk = localStorage.getItem("token");
    setEnviandoContrato(true);
    fetch(`/api/contratos/${contratoSelecionado.id}/versoes`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ templateId: templateVinculado.id, notasAlteracao: notasContrato, solicitante: solicitanteContrato }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          tratarErroResposta(d, "Erro ao gerar.");
          return;
        }
        setTemplateVinculado(null);
        setNotasContrato("");
        setSolicitanteContrato("");
        carregarVersoesContrato(contratoSelecionado.id);
        carregarContratos();
        mostrarSnack("Nova versão do contrato gerada com sucesso!");
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"))
      .finally(() => setEnviandoContrato(false));
  };

  const baixarArquivoContrato = useCallback(
    (url, nomeArquivo) => {
      const tk = localStorage.getItem("token");
      fetch(url, { headers: { Authorization: `Bearer ${tk}` } })
        .then(async (r) => {
          if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            throw new Error(d.erro || "Erro ao baixar arquivo.");
          }
          return r.blob();
        })
        .then((blob) => {
          const objUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objUrl;
          a.download = nomeArquivo;
          a.click();
          URL.revokeObjectURL(objUrl);
        })
        .catch((e) => mostrarSnack(e.message || "Erro ao baixar arquivo.", "error"));
    },
    [mostrarSnack],
  );

  // ── Visualizar o documento de uma versão do contrato (PDF convertido sob demanda)
  const handleVisualizarDocumentoContrato = useCallback(
    (contratoId, versaoId) => {
      const tk = localStorage.getItem("token");
      setVisualizandoContrato(versaoId);
      fetch(`/api/contratos/${contratoId}/versoes/${versaoId}/download-pdf`, { headers: { Authorization: `Bearer ${tk}` } })
        .then(async (r) => {
          if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            throw new Error(d.erro || "Erro ao gerar PDF do contrato.");
          }
          return r.blob();
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        })
        .catch((e) => mostrarSnack(e.message || "Erro ao visualizar contrato.", "error"))
        .finally(() => setVisualizandoContrato(null));
    },
    [mostrarSnack],
  );
  const handleVisualizarDocumentoAssinado = useCallback(
    async (documento) => {
      if (!documento?.id) {
        mostrarSnack("Documento assinado não encontrado.", "warning");
        return;
      }

      try {
        const tk = localStorage.getItem("token");

        const response = await fetch(`/api/assinatura-final/documentos/${documento.id}/arquivo`, {
          headers: {
            Authorization: `Bearer ${tk}`,
          },
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));

          throw new Error(data.erro || "Erro ao visualizar documento assinado.");
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        window.open(url, "_blank", "noopener,noreferrer");

        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 60000);
      } catch (error) {
        console.error("Erro ao visualizar documento assinado:", error);

        mostrarSnack(error.message || "Erro ao visualizar documento assinado.", "error");
      }
    },
    [mostrarSnack],
  );
  const handleConfirmarExcluirVersao = useCallback((id) => {
    document.activeElement?.blur();
    setConfirmExcluirVersao({ aberto: true, id });
  }, []);

  const handleExcluirVersaoContrato = () => {
    const { id } = confirmExcluirVersao;
    setConfirmExcluirVersao({ aberto: false, id: null });
    const tk = localStorage.getItem("token");
    fetch(`/api/contratos/${contratoSelecionado.id}/versoes/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(async (res) => {
        if (!res.ok && res.status !== 204) {
          const d = await res.json().catch(() => ({}));
          mostrarSnack(d.erro || "Erro ao excluir.", "error");
          return;
        }
        carregarVersoesContrato(contratoSelecionado.id);
        carregarContratos();
        mostrarSnack("Versão excluída.", "warning");
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  // ── Exclusão definitiva e arquivamento de contratos
  const handleAbrirExcluirContrato = useCallback((id) => {
    document.activeElement?.blur();
    setConfirmExcluirContrato({ aberto: true, id });
  }, []);

  const handleAbrirArquivarContrato = useCallback((id) => {
    document.activeElement?.blur();
    setConfirmArquivarContrato({ aberto: true, id });
  }, []);

  const handleExcluirContrato = async () => {
    const { id } = confirmExcluirContrato;
    if (!id || processandoRetiradaContrato || !podeExcluir) return;

    setProcessandoRetiradaContrato(true);

    try {
      const tk = localStorage.getItem("token");

      const res = await fetch(`/api/contratos/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tk}` },
      });

      if (!res.ok) {
        const dados = await res.json().catch(() => ({}));
        throw new Error(dados.erro || "Erro ao excluir contrato.");
      }

      setConfirmExcluirContrato({ aberto: false, id: null });

      if (contratoSelecionado?.id === id) {
        handleFecharDetalhe();
      }

      carregarContratos();

      mostrarSnack("Contrato excluído. A Proposta Comercial foi liberada para um novo contrato.");
    } catch (erro) {
      mostrarSnack(erro.message || "Erro de conexão ao excluir contrato.", "error");
    } finally {
      setProcessandoRetiradaContrato(false);
    }
  };

  const handleArquivarContrato = async () => {
    const { id } = confirmArquivarContrato;
    if (!id || processandoRetiradaContrato || !podeExcluir) return;

    setProcessandoRetiradaContrato(true);

    try {
      const tk = localStorage.getItem("token");

      const res = await fetch(`/api/contratos/${id}/arquivar`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tk}` },
      });

      if (!res.ok) {
        const dados = await res.json().catch(() => ({}));
        throw new Error(dados.erro || "Erro ao arquivar contrato.");
      }

      setConfirmArquivarContrato({ aberto: false, id: null });

      if (contratoSelecionado?.id === id) {
        handleFecharDetalhe();
      }

      carregarContratos();

      mostrarSnack("Contrato arquivado com sucesso. Documentos e histórico preservados.");
    } catch (erro) {
      mostrarSnack(erro.message || "Erro de conexão ao arquivar contrato.", "error");
    } finally {
      setProcessandoRetiradaContrato(false);
    }
  };

  const handleInutilizar = (motivo) => {
    const { id } = confirmInutilizar;
    if (!motivo?.trim()) {
      mostrarSnack("Motivo é obrigatório.", "error");
      return;
    }
    setConfirmInutilizar({ aberto: false, id: null });
    const tk = localStorage.getItem("token");
    fetch(`/api/contratos/${id}/inutilizar`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ motivo }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro ao inutilizar.", "error");
          return;
        }
        if (contratoSelecionado?.id === id) handleFecharDetalhe();
        mostrarSnack("Contrato inutilizado.", "warning");
        carregarContratos();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  const handleAbrirInutilizar = useCallback((id) => {
    document.activeElement?.blur();
    setConfirmInutilizar({ aberto: true, id });
  }, []);

  // ── Ativar (reverte um contrato inutilizado, volta pro status de antes da inutilização)
  const handleAtivar = () => {
    const { id, motivo } = confirmAtivar;
    if (!motivo?.trim()) {
      mostrarSnack("Motivo é obrigatório.", "error");
      return;
    }
    setConfirmAtivar({ aberto: false, id: null, motivoInutilizacao: "", motivo: "" });
    const tk = localStorage.getItem("token");
    fetch(`/api/contratos/${id}/ativar`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify({ motivo }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro ao ativar.", "error");
          return;
        }
        if (contratoSelecionado?.id === id) handleFecharDetalhe();
        mostrarSnack("Contrato ativado.", "success");
        carregarContratos();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  const handleAbrirAtivar = useCallback((id, motivoInutilizacao) => {
    document.activeElement?.blur();
    setConfirmAtivar({ aberto: true, id, motivoInutilizacao: motivoInutilizacao ?? "", motivo: "" });
  }, []);

  const modoNovo = Boolean(contratoSelecionado?.__novo);
  // Contrato originado de Template que teve todas as versões excluídas — reabre o
  // seletor de Template em vez do upload manual, pra permitir gerar outro documento.
  const precisaSelecionarTemplate = !modoNovo && !!contratoSelecionado?.templateId && versoesContrato.length === 0;

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Contratos
        </Typography>

        {filtroDashboard && (
          <Box sx={{ mb: 1 }}>
            <Chip
              label={
                filtroDashboard.tipo === "pendenciaAprovacao"
                  ? `Filtrado por: ${LABEL_PENDENCIA_APROVACAO[filtroDashboard.valor] ?? filtroDashboard.valor}${filtroDashboard.geral ? " (todos os usuários)" : ""}`
                  : "Filtrado por: Meus Contratos"
              }
              onDelete={handleLimparFiltroDashboard}
              sx={{ backgroundColor: "#1998a8", color: "#fff", fontWeight: "bold" }}
            />
          </Box>
        )}

        {/* ── Filtro + Copiar + CSV ───────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Filtrar Contrato"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePesquisar();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
                color: "var(--ig-t-ffffff)",
              },
              "& input::placeholder": {
                color: "var(--ig-t-ffffff)",
                opacity: 1,
                fontWeight: 500,
                fontSize: "1rem",
              },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#42a5f5",
                backgroundColor: "rgba(33, 150, 243, 0.08)",
              },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            <i
              className="fas fa-copy"
              style={{
                marginRight: 6,
              }}
            />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            CSV
          </Button>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* ── Grid ─────────────────────────────────────────────────────────── */}
        <GridContratos
          gridMinimizado={gridMinimizado}
          carregando={carregando}
          items={items}
          total={total}
          pagina={pagina}
          porPagina={porPagina}
          contratoSelecionadoId={contratoSelecionado?.id}
          podeVisualizar={podeVisualizar}
          podeExcluir={podeExcluir}
          onVisualizar={handleVisualizar}
          onAbrirExcluir={handleAbrirExcluirContrato}
          onAbrirArquivar={handleAbrirArquivarContrato}
          onAbrirAtivar={handleAbrirAtivar}
          setPagina={setPagina}
          setPorPagina={setPorPagina}
        />

        {/* ── Painel de detalhe (novo contrato OU versões do contrato selecionado) ── */}
        {(podeCadastrar || (podeVisualizar && contratoSelecionado && !modoNovo)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            {modoNovo ? (
              <>
                <Typography variant="h6" sx={{ mb: 2, color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
                  Novo Contrato
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <CampoSelecionar
                    label="Vincular Proposta Comercial"
                    valor={propostaVinculada?.codigo ?? ""}
                    onAbrir={() => setModalPropostaAberto(true)}
                    onLimpar={() => {
                      setPropostaVinculada(null);
                      setTemplateVinculado(null);
                    }}
                    fullWidth
                  />

                  {propostaVinculada ? (
                    <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.85rem" }}>
                      O título do contrato é gerado automaticamente a partir dos dados da Proposta (imóvel, locadora, locatária, data de início e versão).
                    </Typography>
                  ) : (
                    <TextField
                      label="Título *"
                      fullWidth
                      value={tituloNovo}
                      onChange={(e) => setTituloNovo(e.target.value.toUpperCase())}
                      sx={{
                        "& .MuiInputLabel-root": { color: "var(--ig-t-ffffff)", fontWeight: "bold" },
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "var(--ig-f-2a2a2a)",
                          color: "var(--ig-t-ffffff)",
                          "& fieldset": { borderColor: "var(--ig-b-444444)" },
                          "&:hover fieldset": { borderColor: "var(--ig-b-888888)" },
                        },
                        "& input": { color: "var(--ig-t-ffffff)" },
                      }}
                    />
                  )}

                  {propostaVinculada && (
                    <CampoSelecionar
                      label="Template *"
                      valor={templateVinculado?.nome ?? ""}
                      onAbrir={() => setModalTemplateAberto(true)}
                      onLimpar={() => setTemplateVinculado(null)}
                      fullWidth
                    />
                  )}

                  {!propostaVinculada && (
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        border: "1px solid var(--ig-b-333333)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "var(--ig-t-aaaaaa)",
                          fontSize: "0.85rem",
                          mb: 1.5,
                        }}
                      >
                        Arquivo do contrato (.docx):
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          component="label"
                          variant="contained"
                          startIcon={<FaPaperclip />}
                          size="small"
                          sx={{
                            width: "auto",
                            backgroundColor: "#007bff",
                            fontWeight: 700,
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: "#0069d9",
                            },
                          }}
                        >
                          {arquivoContrato ? arquivoContrato.name : "GED"}

                          <input type="file" hidden accept=".docx" onChange={(e) => setArquivoContrato(e.target.files[0] ?? null)} />
                        </Button>
                      </Box>
                    </Paper>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      onClick={handleFecharDetalhe}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<FaSave />}
                      onClick={handleCriarContrato}
                      disabled={criandoContrato}
                      sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                    >
                      {criandoContrato ? "Cadastrando..." : "Cadastrar"}
                    </Button>
                  </Box>
                </Box>

                <ModalSelecionarProposta
                  open={modalPropostaAberto}
                  onClose={() => setModalPropostaAberto(false)}
                  onSelect={(p) => {
                    setPropostaVinculada(p);
                    setTemplateVinculado(null);
                    setModalPropostaAberto(false);
                  }}
                />
                <ModalSelecionarTemplate
                  open={modalTemplateAberto}
                  onClose={() => setModalTemplateAberto(false)}
                  onSelect={(t) => {
                    setTemplateVinculado(t);
                    setArquivoContrato(null);
                    setModalTemplateAberto(false);
                  }}
                />
              </>
            ) : (
              <>
                <Box sx={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap", justifyContent: "space-between", mb: 0.5, gap: 1.5 }}>
                  <Typography variant="h6" sx={{ color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
                    Contrato — {contratoSelecionado.titulo ?? "—"}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.72rem" }}>Status do Contrato:</Typography>
                      {chipStatusAprovacaoContrato(contratoSelecionado.statusAprovacao)}
                    </Box>
                    {contratoSelecionado.statusAprovacao === "EmElaboracao" && podeCadastrar && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SendIcon />}
                        onClick={handleEnviarAprovacao}
                        disabled={enviandoAprovacao}
                        sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold" }}
                      >
                        {enviandoAprovacao ? "Enviando..." : "Enviar para Aprovação"}
                      </Button>
                    )}
                    {contratoSelecionado.statusAprovacao === "Inutilizado" && podeExcluir && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={() => handleAbrirAtivar(contratoSelecionado.id, contratoSelecionado.motivoInutilizacao)}
                        sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold" }}
                      >
                        Ativar
                      </Button>
                    )}
                  </Box>
                </Box>
                <Typography sx={{ color: "var(--ig-t-aaaaaa)", fontSize: "0.85rem", mb: 2 }}>
                  Proposta vinculada: <strong style={{ color: "var(--ig-t-ffffff)" }}>{contratoSelecionado.proposta?.codigo ?? "—"}</strong>
                </Typography>

                <Tabs
                  value={abaAtiva}
                  onChange={(_, v) => setAbaAtiva(v)}
                  sx={{
                    mb: 2,
                    borderBottom: "1px solid var(--ig-b-333333)",
                    "& .MuiTab-root": { color: "var(--ig-t-aaaaaa)", textTransform: "none", fontWeight: 600 },
                    "& .MuiTab-root.Mui-selected": { color: "#1998a8" },
                    "& .MuiTabs-indicator": { backgroundColor: "#1998a8" },
                  }}
                >
                  <Tab value="versoes" label="Versões" />
                  <Tab value="documento" label="Documento" />
                  <Tab value="aprovacao" label="Aprovação" />
                  {contratoSelecionado.motivoInutilizacao && <Tab value="eventos" label="Eventos" />}
                </Tabs>

                {abaAtiva === "versoes" && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TabelaVersoesContrato
                      versoesContrato={versoesContrato}
                      usuariosPorId={usuariosAtivosPorId}
                      contratoId={contratoSelecionado.id}
                      statusAprovacao={contratoSelecionado.statusAprovacao}
                      jaEnviadoAprovacao={contratoSelecionado.jaEnviadoAprovacao}
                      podeExcluir={podeExcluir}
                      visualizandoContrato={visualizandoContrato}
                      onVisualizarDocumento={handleVisualizarDocumentoContrato}
                      onBaixarArquivo={baixarArquivoContrato}
                      onExcluirVersao={handleConfirmarExcluirVersao}
                    />

                    {podeCadastrar && contratoSelecionado.statusAprovacao !== "EmElaboracao" && (
                      <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.82rem", fontStyle: "italic" }}>
                        Contrato em aprovação — para enviar uma nova versão, clique em "↩ Revisar" na aba Aprovação.
                      </Typography>
                    )}

                    {podeCadastrar && contratoSelecionado.statusAprovacao === "EmElaboracao" && somenteLeitura && (
                      <Typography sx={{ color: "var(--ig-t-888888)", fontSize: "0.82rem", fontStyle: "italic" }}>
                        Clique em "Editar" para enviar uma nova versão deste contrato.
                      </Typography>
                    )}

                    {podeCadastrar && contratoSelecionado.statusAprovacao === "EmElaboracao" && !somenteLeitura && (
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: "var(--ig-f-1e1e1e)",
                          border: "1px solid var(--ig-b-333333)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "var(--ig-t-aaaaaa)",
                            fontSize: "0.85rem",
                            mb: 1.5,
                          }}
                        >
                          {precisaSelecionarTemplate
                            ? "Todas as versões foram excluídas — gere novamente pelo Template ou envie um novo arquivo pelo GED:"
                            : "Nova versão do contrato (.docx) — descreva as alterações desta versão:"}
                        </Typography>

                        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                          <InputLabel
                            sx={{
                              color: "var(--ig-t-ffffff)",
                              fontWeight: "bold",
                            }}
                          >
                            Solicitante *
                          </InputLabel>

                          <Select
                            label="Solicitante *"
                            value={solicitanteContrato}
                            onChange={(e) => setSolicitanteContrato(e.target.value)}
                            sx={{
                              backgroundColor: "var(--ig-f-2a2a2a)",
                              color: "var(--ig-t-ffffff)",
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "var(--ig-b-444444)",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "var(--ig-b-888888)",
                              },
                              "& .MuiSvgIcon-root": {
                                color: "var(--ig-t-ffffff)",
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>Selecione</em>
                            </MenuItem>

                            {SOLICITANTES_VERSAO.map((s) => (
                              <MenuItem key={s} value={s}>
                                {s}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          multiline
                          minRows={2}
                          fullWidth
                          inputRef={notasContratoRef}
                          placeholder="Notas sobre as alterações desta versão *"
                          value={notasContrato}
                          onChange={(e) => setNotasContrato(e.target.value)}
                          sx={{
                            mb: 2,
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "var(--ig-f-2a2a2a)",
                              color: "var(--ig-t-ffffff)",
                              "& fieldset": {
                                borderColor: "var(--ig-b-444444)",
                              },
                              "&:hover fieldset": {
                                borderColor: "var(--ig-b-888888)",
                              },
                            },
                            "& textarea": {
                              color: "var(--ig-t-ffffff)",
                            },
                          }}
                        />

                        {precisaSelecionarTemplate && (
                          <>
                            <Box
                              sx={{
                                border: "1px solid var(--ig-b-333333)",
                                borderRadius: 1,
                                p: 2,
                                mb: 2,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "var(--ig-t-ffffff)",
                                  fontWeight: 700,
                                  fontSize: "0.85rem",
                                  mb: 1.5,
                                }}
                              >
                                GERAR PELO TEMPLATE
                              </Typography>

                              <CampoSelecionar
                                label="Template *"
                                valor={templateVinculado?.nome ?? ""}
                                onAbrir={() => setModalTemplateAberto(true)}
                                onLimpar={() => setTemplateVinculado(null)}
                                fullWidth
                                sx={{ mb: 1.5 }}
                              />

                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleEnviarVersaoContratoViaTemplate}
                                  disabled={enviandoContrato}
                                  sx={{
                                    width: "auto",
                                    backgroundColor: "#2e7d32",
                                    "&:hover": {
                                      backgroundColor: "#1b5e20",
                                    },
                                    fontWeight: "bold",
                                    textTransform: "none",
                                  }}
                                >
                                  {enviandoContrato ? "Gerando..." : "Gerar Documento"}
                                </Button>
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                my: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  height: "1px",
                                  backgroundColor: "#444",
                                  flex: 1,
                                }}
                              />

                              <Typography
                                sx={{
                                  color: "var(--ig-t-888888)",
                                  fontWeight: 700,
                                  fontSize: "0.78rem",
                                }}
                              >
                                OU
                              </Typography>

                              <Box
                                sx={{
                                  height: "1px",
                                  backgroundColor: "#444",
                                  flex: 1,
                                }}
                              />
                            </Box>
                          </>
                        )}

                        <Box
                          sx={{
                            border: precisaSelecionarTemplate ? "1px solid var(--ig-b-333333)" : "none",
                            borderRadius: 1,
                            p: precisaSelecionarTemplate ? 2 : 0,
                          }}
                        >
                          {precisaSelecionarTemplate && (
                            <Typography
                              sx={{
                                color: "var(--ig-t-ffffff)",
                                fontWeight: 700,
                                fontSize: "0.85rem",
                                mb: 1.5,
                              }}
                            >
                              ENVIAR NOVO DOCUMENTO
                            </Typography>
                          )}

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Button
                              component="label"
                              variant="contained"
                              startIcon={<FaPaperclip />}
                              size="small"
                              sx={{
                                width: "auto",
                                backgroundColor: "#007bff",
                                fontWeight: 700,
                                textTransform: "none",
                                "&:hover": {
                                  backgroundColor: "#0069d9",
                                },
                              }}
                            >
                              {arquivoContrato ? arquivoContrato.name : "GED"}

                              <input
                                type="file"
                                hidden
                                accept=".docx"
                                onChange={(e) => {
                                  const arquivo = e.target.files[0] ?? null;

                                  setArquivoContrato(arquivo);

                                  if (arquivo) {
                                    setTemplateVinculado(null);
                                  }
                                }}
                              />
                            </Button>

                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleEnviarVersaoContrato}
                              disabled={enviandoContrato}
                              sx={{
                                width: "auto",
                                backgroundColor: "#2e7d32",
                                "&:hover": {
                                  backgroundColor: "#1b5e20",
                                },
                                fontWeight: "bold",
                                textTransform: "none",
                              }}
                            >
                              {enviandoContrato ? "Enviando..." : "Enviar Nova Versão"}
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    )}
                    <ModalSelecionarTemplate
                      open={modalTemplateAberto}
                      onClose={() => setModalTemplateAberto(false)}
                      onSelect={(t) => {
                        setTemplateVinculado(t);
                        setArquivoContrato(null);
                        setModalTemplateAberto(false);
                      }}
                    />
                  </Box>
                )}
                {abaAtiva === "documento" && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "var(--ig-t-ffffff)",
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      Documento Assinado
                    </Typography>

                    {!contratoSelecionado.documentoAssinado ? (
                      <Paper
                        sx={{
                          p: 3,
                          backgroundColor: "var(--ig-f-252525)",
                          border: "1px solid var(--ig-b-333333)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "var(--ig-t-888888)",
                            fontStyle: "italic",
                          }}
                        >
                          Nenhum documento assinado disponível para este contrato.
                        </Typography>
                      </Paper>
                    ) : (
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: "var(--ig-f-252525)",
                          border: "1px solid var(--ig-b-333333)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              color: "var(--ig-t-ffffff)",
                              fontWeight: 600,
                              wordBreak: "break-word",
                            }}
                          >
                            {contratoSelecionado.documentoAssinado.nomeOriginal || "Contrato Assinado.pdf"}
                          </Typography>

                          <Typography
                            sx={{
                              color: "var(--ig-t-aaaaaa)",
                              fontSize: "0.82rem",
                            }}
                          >
                            Documento final assinado
                          </Typography>

                          <Typography
                            sx={{
                              color: "var(--ig-t-888888)",
                              fontSize: "0.78rem",
                            }}
                          >
                            Disponibilizado em:{" "}
                            {contratoSelecionado.documentoAssinado.criadoEm ? formatarDataHora(contratoSelecionado.documentoAssinado.criadoEm) : "—"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0,
                          }}
                        >
                          <Tooltip title="Visualizar documento assinado">
                            <IconButton
                              size="small"
                              onClick={() => handleVisualizarDocumentoAssinado(contratoSelecionado.documentoAssinado)}
                              sx={{
                                backgroundColor: "#1998a8",
                                color: "#fff",
                                borderRadius: "4px",
                                mx: 0.25,
                                width: "auto",
                                "&:hover": {
                                  backgroundColor: "#1565c0",
                                },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Baixar documento assinado">
                            <IconButton
                              size="small"
                              onClick={() =>
                                baixarArquivoContrato(
                                  `/api/assinatura-final/documentos/${contratoSelecionado.documentoAssinado.id}/arquivo`,
                                  contratoSelecionado.documentoAssinado.nomeOriginal || "Contrato Assinado.pdf",
                                )
                              }
                              sx={{
                                backgroundColor: "#555",
                                color: "#fff",
                                borderRadius: "4px",
                                mx: 0.25,
                                width: "auto",
                                "&:hover": {
                                  backgroundColor: "#444",
                                },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    )}
                  </Box>
                )}

                {abaAtiva === "aprovacao" && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <PainelAprovacao
                      titulo="Contrato"
                      statusField={contratoSelecionado.statusAprovacao}
                      statusNaoIniciado="EmElaboracao"
                      mostrarCabecalho={false}
                      mostrarBotaoEnviar={false}
                      aprovacao={aprovacaoInfo.aprovacao}
                      autorizacoes={aprovacaoInfo.autorizacoes}
                      historicos={aprovacaoInfo.historicos}
                      idUsuarioLogado={userId}
                      idCriador={contratoSelecionado.idUsuarioCriacao ?? null}
                      usuarioEhAprovador={aprovacaoInfo.usuarioEhAprovador}
                      revisarPorAlcadaOuCriador
                      usuarioEmAlcada={aprovacaoInfo.usuarioEmAlcada}
                      onAprovar={handleAprovar}
                      onAbrirReprovar={() => {
                        document.activeElement?.blur();
                        setDialogReprovar({ aberto: true, motivo: "" });
                      }}
                      onAbrirQuestionar={() => {
                        document.activeElement?.blur();
                        setDialogQuestionar({ aberto: true, motivo: "", idsUsuarioResposta: [], arquivo: null });
                      }}
                      onAbrirResponder={(pergunta) => setDialogResponder({ aberto: true, resposta: "", pergunta, arquivo: null })}
                      onConfirmarRevisar={() => {
                        document.activeElement?.blur();
                        setConfirmRevisar({ aberto: true, motivo: "" });
                      }}
                      processando={processandoAprovacao}
                      podeEditar={podeCadastrar}
                      rotaAnexoBase="/api/contratos"
                    />
                  </Box>
                )}

                {abaAtiva === "eventos" && contratoSelecionado.motivoInutilizacao && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ backgroundColor: "var(--ig-f-2a1f1f)", border: "1px solid var(--ig-b-4e342e)", borderRadius: 1, p: 2 }}>
                      <Typography sx={{ color: "var(--ig-t-cccccc)", fontWeight: 700, fontSize: "0.8rem", mb: 0.5, letterSpacing: 0.5 }}>
                        HISTÓRICO DE INUTILIZAÇÃO
                      </Typography>
                      <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.85rem" }}>
                        Inutilizado em <strong style={{ color: "var(--ig-t-ffffff)" }}>{formatarDataHora(contratoSelecionado.dataInutilizacao)}</strong>
                        {contratoSelecionado.idUsuarioInutilizacao && (
                          <>
                            {" "}
                            por{" "}
                            <strong style={{ color: "var(--ig-t-ffffff)" }}>
                              {usuariosAtivosPorId.get(contratoSelecionado.idUsuarioInutilizacao)?.nome ?? "—"}
                            </strong>
                          </>
                        )}{" "}
                        — Motivo: <strong style={{ color: "var(--ig-t-ffffff)" }}>{contratoSelecionado.motivoInutilizacao}</strong>
                      </Typography>
                      {contratoSelecionado.dataReativacao && (
                        <Typography sx={{ color: "var(--ig-t-cccccc)", fontSize: "0.85rem", mt: 0.5 }}>
                          Reativado em <strong style={{ color: "var(--ig-t-ffffff)" }}>{formatarDataHora(contratoSelecionado.dataReativacao)}</strong>
                          {contratoSelecionado.idUsuarioReativacao && (
                            <>
                              {" "}
                              por{" "}
                              <strong style={{ color: "var(--ig-t-ffffff)" }}>
                                {usuariosAtivosPorId.get(contratoSelecionado.idUsuarioReativacao)?.nome ?? "—"}
                              </strong>
                            </>
                          )}
                          {contratoSelecionado.motivoReativacao && (
                            <>
                              {" "}
                              — Motivo: <strong style={{ color: "var(--ig-t-ffffff)" }}>{contratoSelecionado.motivoReativacao}</strong>
                            </>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
                  {somenteLeitura ? (
                    <>
                      <Button
                        variant="outlined"
                        onClick={handleFecharDetalhe}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",
                          "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Fechar
                      </Button>
                      {podeCadastrar && contratoSelecionado.statusAprovacao === "EmElaboracao" && (
                        <Button
                          variant="contained"
                          startIcon={<FaEdit />}
                          onClick={handleHabilitarEdicao}
                          sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", px: 3 }}
                        >
                          Editar
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<FaTimes />}
                      onClick={handleFecharDetalhe}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Cancelar
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Paper>
        )}

        <Dialog open={confirmExcluirVersao.aberto} onClose={() => setConfirmExcluirVersao({ aberto: false, id: null })}>
          <DialogTitle>Confirmar exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>Deseja excluir esta versão do contrato? Esta ação não pode ser desfeita.</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={() => setConfirmExcluirVersao({ aberto: false, id: null })}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={handleExcluirVersaoContrato}>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={confirmExcluirContrato.aberto}
          onClose={() => {
            if (!processandoRetiradaContrato) {
              setConfirmExcluirContrato({ aberto: false, id: null });
            }
          }}
        >
          <DialogTitle>Excluir Contrato</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Deseja excluir definitivamente este contrato e suas versões? Esta operação não poderá ser desfeita. A Proposta Comercial vinculada ficará
              disponível para iniciar outro contrato.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" disabled={processandoRetiradaContrato} onClick={() => setConfirmExcluirContrato({ aberto: false, id: null })}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={handleExcluirContrato} disabled={processandoRetiradaContrato}>
              {processandoRetiradaContrato ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmArquivarContrato.aberto}
          onClose={() => {
            if (!processandoRetiradaContrato) {
              setConfirmArquivarContrato({ aberto: false, id: null });
            }
          }}
        >
          <DialogTitle>Arquivar Contrato</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Deseja arquivar este contrato? Todas as versões, os documentos e o histórico de aprovação serão preservados. A Proposta Comercial permanecerá
              vinculada.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" disabled={processandoRetiradaContrato} onClick={() => setConfirmArquivarContrato({ aberto: false, id: null })}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleArquivarContrato} disabled={processandoRetiradaContrato}>
              {processandoRetiradaContrato ? "Arquivando..." : "Arquivar"}
            </Button>
          </DialogActions>
        </Dialog>
        <DialogInutilizarContrato
          aberto={confirmInutilizar.aberto}
          onCancelar={() => setConfirmInutilizar({ aberto: false, id: null })}
          onConfirmar={handleInutilizar}
        />

        <DialogErroTemplate
          aberto={dialogErroTemplate.aberto}
          detalhes={dialogErroTemplate.detalhes}
          onFechar={() => setDialogErroTemplate({ aberto: false, detalhes: [] })}
        />

        <DialogReprovarAprovacao
          aberto={confirmAtivar.aberto}
          motivo={confirmAtivar.motivo}
          onChangeMotivo={(motivo) => setConfirmAtivar((p) => ({ ...p, motivo }))}
          onCancelar={() => setConfirmAtivar({ aberto: false, id: null, motivoInutilizacao: "", motivo: "" })}
          onConfirmar={handleAtivar}
          titulo="Ativar Contrato"
          contentText={`O contrato voltará para o status anterior à inutilização.${confirmAtivar.motivoInutilizacao ? ` Motivo da inutilização: ${confirmAtivar.motivoInutilizacao}` : ""} Informe o motivo da reativação:`}
          rows={4}
          maxLength={255}
          labelConfirmar="Ativar"
          corConfirmar="#2e7d32"
        />

        <DialogReprovarAprovacao
          aberto={dialogReprovar.aberto}
          motivo={dialogReprovar.motivo}
          onChangeMotivo={(motivo) => setDialogReprovar((p) => ({ ...p, motivo }))}
          onCancelar={() => setDialogReprovar({ aberto: false, motivo: "" })}
          onConfirmar={handleReprovar}
          processando={processandoAprovacao}
          titulo="Reprovar Contrato"
        />

        <DialogQuestionarAprovacao
          aberto={dialogQuestionar.aberto}
          motivo={dialogQuestionar.motivo}
          onChangeMotivo={(motivo) => setDialogQuestionar((p) => ({ ...p, motivo }))}
          idsUsuarioResposta={dialogQuestionar.idsUsuarioResposta}
          onChangeUsuariosResposta={(idsUsuarioResposta) => setDialogQuestionar((p) => ({ ...p, idsUsuarioResposta }))}
          arquivo={dialogQuestionar.arquivo}
          onChangeArquivo={(arquivo) => setDialogQuestionar((p) => ({ ...p, arquivo }))}
          usuariosAtivos={usuariosAprovadores}
          onCancelar={() => setDialogQuestionar({ aberto: false, motivo: "", idsUsuarioResposta: [], arquivo: null })}
          onConfirmar={handleQuestionar}
          processando={processandoAprovacao}
          titulo="Questionar Contrato"
        />

        <DialogResponderAprovacao
          aberto={dialogResponder.aberto}
          resposta={dialogResponder.resposta}
          pergunta={dialogResponder.pergunta}
          onChangeResposta={(resposta) => setDialogResponder((p) => ({ ...p, resposta }))}
          arquivo={dialogResponder.arquivo}
          onChangeArquivo={(arquivo) => setDialogResponder((p) => ({ ...p, arquivo }))}
          onCancelar={() => setDialogResponder({ aberto: false, resposta: "", pergunta: "", arquivo: null })}
          onConfirmar={handleResponder}
          processando={processandoAprovacao}
          titulo="Responder Questão — Contrato"
        />

        <DialogReprovarAprovacao
          aberto={confirmRevisar.aberto}
          motivo={confirmRevisar.motivo}
          onChangeMotivo={(motivo) => setConfirmRevisar((p) => ({ ...p, motivo }))}
          onCancelar={() => setConfirmRevisar({ aberto: false, motivo: "" })}
          onConfirmar={handleRevisar}
          processando={processandoAprovacao}
          titulo="Solicitar Revisão"
          contentText='Ao revisar, todas as aprovações do fluxo atual serão invalidadas e o contrato voltará para "Em Elaboração". Informe o motivo da revisão:'
          rows={4}
          maxLength={255}
          labelConfirmar="Confirmar"
          corConfirmar="#e65100"
        />

        <Snackbar
          open={snack.aberto}
          autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, aberto: false }))}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
